/**
 * Jaipur Handloom Saved Cart Recovery v2.3 / AE26
 * Product-rich cart capture, resilient loading, portable restoration,
 * optional transactional email endpoint and re-engagement event hooks.
 */
(function() {
  'use strict';

  var STORAGE_KEY = 'jh_saved_cart_recovery_v2';
  var RESTORE_PARAM = 'jh_saved_cart';
  var root = document.querySelector('[data-jh-saved-cart]');
  if (!root) return;

  var JH = window.JH = window.JH || {};
  var U = JH.Utils || {};
  var A11y = JH.A11y || {};
  var state = {
    opener: null,
    cart: null,
    record: null,
    busy: false,
    restoredOnLoad: false,
    loadId: 0,
    focusTimer: null
  };

  function qs(selector, context) {
    return (context || document).querySelector(selector);
  }

  function qsa(selector, context) {
    return Array.prototype.slice.call((context || document).querySelectorAll(selector));
  }

  function route(path) {
    var base = root.getAttribute('data-root-url') || '/';
    if (base.charAt(base.length - 1) !== '/') base += '/';
    return base + String(path || '').replace(/^\/+/, '');
  }

  function absoluteUrl(value) {
    value = String(value || '');
    if (!value) return '';
    try { return new URL(value, window.location.origin).toString(); }
    catch (error) { return value; }
  }

  function escapeHtml(value) {
    if (typeof U.escapeHtml === 'function') return U.escapeHtml(value);
    return String(value == null ? '' : value).replace(/[&<>"']/g, function(character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  function money(cents) {
    if (typeof U.money === 'function') return U.money(cents);
    var currency = root.getAttribute('data-currency') || 'USD';
    try {
      return (Number(cents || 0) / 100).toLocaleString(undefined, { style: 'currency', currency: currency });
    } catch (error) {
      return (Number(cents || 0) / 100).toFixed(2) + ' ' + currency;
    }
  }

  function emit(name, detail) {
    if (typeof U.emit === 'function') U.emit(name, detail);
    else {
      try { document.dispatchEvent(new CustomEvent(name, { detail: detail })); } catch (error) {}
    }
  }

  function track(eventName, detail) {
    var payload = detail || {};
    payload.event = eventName;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (error) {}
    emit('jh:analytics:' + eventName, payload);
  }

  function readStorage() {
    try {
      var value = localStorage.getItem(STORAGE_KEY);
      var record = value ? JSON.parse(value) : null;
      if (record && record.expires_at && Date.parse(record.expires_at) < Date.now()) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return record;
    } catch (error) {
      return null;
    }
  }

  function writeStorage(value) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(value)); } catch (error) {}
  }

  function readInitialCart() {
    var node = qs('[data-jh-saved-cart-initial-cart]', root);
    if (!node) return null;
    try {
      var cart = JSON.parse(node.textContent || '{}');
      return cart && Array.isArray(cart.items) ? cart : null;
    } catch (error) {
      return null;
    }
  }

  function randomId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    return 'jhsc-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  function withTimeout(promise, milliseconds, message) {
    return new Promise(function(resolve, reject) {
      var settled = false;
      var timer = window.setTimeout(function() {
        if (settled) return;
        settled = true;
        reject(new Error(message || 'Request timed out'));
      }, milliseconds);

      Promise.resolve(promise).then(function(value) {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve(value);
      }, function(error) {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        reject(error);
      });
    });
  }

  function fetchCartDirect() {
    var configured = String(root.getAttribute('data-cart-json-url') || '').trim();
    var urls = [];
    if (configured) urls.push(configured);
    var localeAware = route('cart.js');
    if (urls.indexOf(localeAware) === -1) urls.push(localeAware);
    if (urls.indexOf('/cart.js') === -1) urls.push('/cart.js');

    function attempt(index) {
      if (index >= urls.length) return Promise.reject(new Error('Unable to read cart'));
      var controller = window.AbortController ? new AbortController() : null;
      var timer = window.setTimeout(function() { if (controller) controller.abort(); }, 4500);
      return fetch(urls[index] + (urls[index].indexOf('?') === -1 ? '?' : '&') + 't=' + Date.now(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal: controller ? controller.signal : undefined
      }).then(function(response) {
        window.clearTimeout(timer);
        if (!response.ok) throw new Error('Unable to read cart (' + response.status + ')');
        return response.json();
      }, function(error) {
        window.clearTimeout(timer);
        throw error;
      }).catch(function() {
        return attempt(index + 1);
      });
    }

    return attempt(0);
  }

  function cartWrapperFallback() {
    var promise = null;
    try {
      if (JH.Cart && typeof JH.Cart.get === 'function') promise = JH.Cart.get();
      else if (window.JHSmartCart && typeof window.JHSmartCart.get === 'function') promise = window.JHSmartCart.get();
    } catch (error) {
      promise = Promise.reject(error);
    }
    return promise ? withTimeout(promise, 3500, 'Theme cart fallback timed out') : Promise.reject(new Error('No cart fallback available'));
  }

  function getCart() {
    return withTimeout(fetchCartDirect(), 6000, 'Cart refresh took too long').catch(function(primaryError) {
      return cartWrapperFallback().catch(function() { throw primaryError; });
    });
  }

  function isRug(item) {
    var text = [item.product_type, item.product_title, item.title, item.handle, item.vendor].join(' ').toLowerCase();
    return /(^|\s|[-_/])(rug|rugs|doormat|runner|dhurrie|carpet)(\s|$|[-_/])/.test(text) || /jute|hemp rug|cotton rug|wool rug/.test(text);
  }

  function rugCount(cart) {
    return ((cart && cart.items) || []).reduce(function(total, item) {
      return total + (isRug(item) ? Number(item.quantity || 1) : 0);
    }, 0);
  }

  function base64UrlEncode(value) {
    var binary = unescape(encodeURIComponent(value));
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }

  function base64UrlDecode(value) {
    var normalized = String(value || '').replace(/-/g, '+').replace(/_/g, '/');
    while (normalized.length % 4) normalized += '=';
    return decodeURIComponent(escape(atob(normalized)));
  }

  function restorePayload(cart) {
    return {
      v: 2,
      i: ((cart && cart.items) || []).slice(0, 20).map(function(item) {
        var line = {
          id: Number(item.variant_id || item.id),
          q: Math.max(1, Number(item.quantity || 1))
        };
        if (item.properties && Object.keys(item.properties).length) line.p = item.properties;
        if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
          line.s = item.selling_plan_allocation.selling_plan.id;
        }
        return line;
      })
    };
  }

  function createRecoveryUrl(cart) {
    var payload = base64UrlEncode(JSON.stringify(restorePayload(cart)));
    return window.location.origin + route('cart') + '?' + RESTORE_PARAM + '=' + encodeURIComponent(payload);
  }

  function createDiscountUrl(code, recoveryUrl) {
    code = String(code || '').trim();
    if (!code) return recoveryUrl;
    var target = new URL(recoveryUrl, window.location.origin);
    return window.location.origin + route('discount/' + encodeURIComponent(code)) + '?redirect=' + encodeURIComponent(target.pathname + target.search);
  }

  function productUrl(item) {
    var url = item.url || (item.handle ? route('products/' + item.handle) : route('cart'));
    return absoluteUrl(url);
  }

  function imageUrl(item) {
    var image = item.image || (item.featured_image && (item.featured_image.url || item.featured_image.src)) || '';
    if (image && image.indexOf('//') === 0) return window.location.protocol + image;
    return absoluteUrl(image);
  }

  function setLoadingState(name, message) {
    var loading = qs('[data-jh-saved-cart-loading]', root);
    var loadError = qs('[data-jh-saved-cart-load-error]', root);
    var loadMessage = qs('[data-jh-saved-cart-load-error-message]', root);
    var panel = qs('.jh-saved-cart__panel', root);
    if (loading) loading.hidden = true;
    if (loadError) loadError.hidden = name !== 'error';
    if (loadMessage && message) loadMessage.textContent = message;
    if (panel) panel.setAttribute('aria-busy', 'false');
  }

  function setRefreshNote(message) {
    var panel = qs('.jh-saved-cart__panel', root);
    if (!panel) return;
    var note = qs('[data-jh-saved-cart-refresh-note]', panel);
    if (!message) {
      if (note && note.parentNode) note.parentNode.removeChild(note);
      return;
    }
    if (!note) {
      note = document.createElement('p');
      note.className = 'jh-saved-cart__refresh-note';
      note.setAttribute('data-jh-saved-cart-refresh-note', '');
      var hero = qs('.jh-saved-cart__hero', panel);
      if (hero && hero.parentNode) hero.parentNode.insertBefore(note, hero.nextSibling);
    }
    note.textContent = message;
  }

  function renderCart(cart) {
    state.cart = cart || { items: [], item_count: 0, total_price: 0 };
    var content = qs('[data-jh-saved-cart-content]', root);
    var empty = qs('[data-jh-saved-cart-empty]', root);
    var items = state.cart.items || [];

    setLoadingState('ready');
    if (!items.length) {
      if (content) content.hidden = true;
      if (empty) empty.hidden = false;
      return;
    }

    if (empty) empty.hidden = true;
    if (content) content.hidden = false;

    var count = Number(state.cart.item_count || items.reduce(function(total, item) { return total + Number(item.quantity || 1); }, 0));
    var countNode = qs('[data-jh-saved-cart-count]', root);
    var suffix = qs('[data-jh-saved-cart-count-suffix]', root);
    var totalNode = qs('[data-jh-saved-cart-total]', root);
    if (countNode) countNode.textContent = String(count);
    if (suffix) suffix.textContent = count === 1 ? '' : 's';
    if (totalNode) totalNode.textContent = money(state.cart.total_price);

    var list = qs('[data-jh-saved-cart-items]', root);
    if (list) {
      list.innerHTML = items.slice(0, 8).map(function(item) {
        var variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
        var image = imageUrl(item);
        var title = item.product_title || item.title || 'Handmade item';
        return '<article class="jh-saved-cart__item">' +
          '<a class="jh-saved-cart__item-image" href="' + escapeHtml(productUrl(item)) + '" tabindex="-1">' +
            (image ? '<img src="' + escapeHtml(image) + '" alt="' + escapeHtml(title) + '" loading="lazy" width="188" height="235">' : '<span class="jh-saved-cart__item-image-fallback" aria-hidden="true">✦</span>') +
          '</a>' +
          '<div class="jh-saved-cart__item-copy">' +
            '<a href="' + escapeHtml(productUrl(item)) + '">' + escapeHtml(title) + '</a>' +
            (variant ? '<p>' + escapeHtml(variant) + '</p>' : '') +
            '<small>Quantity: ' + escapeHtml(item.quantity || 1) + '</small>' +
          '</div>' +
          '<strong class="jh-saved-cart__item-price">' + escapeHtml(money(item.final_line_price != null ? item.final_line_price : item.line_price)) + '</strong>' +
        '</article>';
      }).join('');
    }

    var rugs = rugCount(state.cart);
    var offer = qs('[data-jh-saved-cart-offer]', root);
    var offerTitle = qs('[data-jh-saved-cart-offer-title]', root);
    var offerText = qs('[data-jh-saved-cart-offer-text]', root);
    var oneCode = root.getAttribute('data-one-code') || '10OFF';
    var twoCode = root.getAttribute('data-two-code') || '20OFF';
    if (offer && offerTitle && offerText) {
      offer.hidden = false;
      if (rugs >= 2) {
        offerTitle.textContent = twoCode + ' is unlocked';
        offerText.textContent = 'Your cart contains 2 or more rugs. Use ' + twoCode + ' for the eligible multi-rug offer.';
      } else if (rugs === 1) {
        offerTitle.textContent = 'Your storewide offer: ' + oneCode;
        offerText.textContent = 'Add one more rug to unlock ' + twoCode + ' on eligible rugs.';
      } else {
        offerTitle.textContent = 'Save now, decide later';
        offerText.textContent = 'Your exact variants and quantities will be included in the return link.';
      }
    }
  }

  function setView(name) {
    var formView = qs('[data-jh-saved-cart-form-view]', root);
    var success = qs('[data-jh-saved-cart-success]', root);
    if (formView) formView.hidden = name === 'success';
    if (success) success.hidden = name !== 'success';
  }

  function showError(message) {
    var error = qs('[data-jh-saved-cart-error]', root);
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  }

  function endpointConfigured() {
    return Boolean(String(root.getAttribute('data-endpoint') || '').trim());
  }

  function isDesignMode() {
    return root.getAttribute('data-design-mode') === 'true' || Boolean(window.Shopify && window.Shopify.designMode);
  }

  function configureDeliveryReadiness() {
    var node = qs('[data-jh-saved-cart-email-readiness]', root);
    var label = qs('[data-jh-saved-cart-submit-label]', root);
    var configuredLabel = root.getAttribute('data-configured-button-label') || 'Save and email my cart';
    if (endpointConfigured()) {
      if (node) {
        node.textContent = 'Email delivery is connected. Your saved-cart message will be queued immediately.';
        node.setAttribute('data-state', 'connected');
      }
      root.setAttribute('data-button-label', configuredLabel);
      if (label && !state.busy) label.textContent = configuredLabel;
    } else {
      if (node) {
        node.textContent = isDesignMode() ?
          'Preview test: the cart and recovery link will save, but no customer email is sent until the recovery endpoint is connected.' :
          'Saved-cart email delivery is not connected yet. The cart will still be saved locally and a recovery link will be created.';
        node.setAttribute('data-state', 'warning');
      }
      root.setAttribute('data-button-label', 'Save my cart and create link');
      if (label && !state.busy) label.textContent = 'Save my cart and create link';
    }
  }

  function setBusy(busy) {
    state.busy = busy;
    var button = qs('[data-jh-saved-cart-submit]', root);
    var label = qs('[data-jh-saved-cart-submit-label]', root);
    if (button) button.disabled = busy;
    if (label) label.textContent = busy ? 'Saving your cart…' : (root.getAttribute('data-button-label') || 'Save my cart');
  }

  function focusModalAfterLoad(preferred) {
    window.clearTimeout(state.focusTimer);
    state.focusTimer = window.setTimeout(function() {
      var modal = qs('[data-jh-saved-cart-modal]', root);
      if (!modal || modal.hidden) return;
      var target = preferred || qs('#JHSavedCartEmail', root) || qs('[data-jh-saved-cart-retry]', root) || modal;
      if (typeof A11y.activateDialog === 'function') A11y.activateDialog(modal, state.opener, target);
      else if (target && typeof target.focus === 'function') target.focus();
    }, 0);
  }

  function loadCartForModal(options) {
    options = options || {};
    var requestId = ++state.loadId;
    var initial = state.cart || readInitialCart();

    if (initial) {
      state.cart = initial;
      renderCart(initial);
    } else {
      setLoadingState('error', 'We could not read this cart. Please open the full cart page and try again.');
    }

    setRefreshNote('Checking for the latest cart changes…');
    return getCart().then(function(cart) {
      if (requestId !== state.loadId) return cart;
      renderCart(cart);
      setRefreshNote('');
      if (options.focus) focusModalAfterLoad(qs('#JHSavedCartEmail', root));
      return cart;
    }).catch(function(error) {
      if (requestId !== state.loadId) return state.cart;
      setRefreshNote(state.cart ? 'Showing the cart already loaded on this page. Live refresh is temporarily unavailable.' : 'Live cart refresh is temporarily unavailable.');
      if (!state.cart) setLoadingState('error', 'We could not read this cart. Please retry, or open the full cart page.');
      if (options.focus) focusModalAfterLoad(state.cart ? qs('#JHSavedCartEmail', root) : qs('[data-jh-saved-cart-retry]', root));
      track('saved_cart_load_error', { message: error && error.message ? error.message : 'Unknown cart loading error' });
      return state.cart;
    });
  }

  function open(opener) {
    var modal = qs('[data-jh-saved-cart-modal]', root);
    if (!modal) return;
    state.opener = opener || document.activeElement;
    setView('form');
    showError('');
    configureDeliveryReadiness();

    if (!state.cart) state.cart = readInitialCart();
    if (state.cart) renderCart(state.cart);
    else setLoadingState('error', 'We could not read this cart. Please open the full cart page and try again.');

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('jh-saved-cart-open');

    var email = qs('#JHSavedCartEmail', root);
    if (email && !email.value) {
      var saved = readStorage();
      email.value = root.getAttribute('data-customer-email') || (saved && saved.email) || '';
    }

    /* Hard safety guard: this component never leaves a blocking loader visible. */
    window.setTimeout(function() {
      var loading = qs('[data-jh-saved-cart-loading]', root);
      if (loading) loading.hidden = true;
    }, 50);

    track('saved_cart_open', { source: opener && opener.getAttribute ? (opener.getAttribute('data-source') || opener.className || 'button') : 'unknown' });
    loadCartForModal({ focus: true }).catch(function() {});
  }

  function close() {
    var modal = qs('[data-jh-saved-cart-modal]', root);
    if (!modal || modal.hidden) return;
    state.loadId += 1;
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('jh-saved-cart-open');
    if (typeof A11y.deactivateDialog === 'function') A11y.deactivateDialog(modal, state.opener);
    else if (state.opener && document.contains(state.opener)) state.opener.focus();
    state.opener = null;
  }

  function serializeItems(cart) {
    return ((cart && cart.items) || []).slice(0, 20).map(function(item) {
      return {
        variant_id: Number(item.variant_id || item.id),
        product_id: Number(item.product_id || 0),
        title: item.product_title || item.title || '',
        variant: item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '',
        quantity: Number(item.quantity || 1),
        unit_price: Number(item.final_price != null ? item.final_price : item.price || 0),
        line_price: Number(item.final_line_price != null ? item.final_line_price : item.line_price || 0),
        url: productUrl(item),
        image: imageUrl(item),
        product_type: item.product_type || '',
        handle: item.handle || '',
        properties: item.properties || {}
      };
    });
  }

  function collectionDirectory() {
    var script = qs('[data-jh-saved-cart-collection-data]', root);
    if (!script) return [];
    try {
      var items = JSON.parse(script.textContent || '[]');
      return items.slice(0, 24).map(function(item) {
        return {
          title: String(item.title || ''),
          url: absoluteUrl(item.url || ''),
          image: absoluteUrl(item.image || '')
        };
      }).filter(function(item) { return item.title && item.url; });
    } catch (error) {
      return [];
    }
  }

  function attribution() {
    var params;
    try { params = new URL(window.location.href).searchParams; } catch (error) { params = null; }
    function get(name) { return params ? String(params.get(name) || '') : ''; }
    return {
      utm_source: get('utm_source'),
      utm_medium: get('utm_medium'),
      utm_campaign: get('utm_campaign'),
      utm_content: get('utm_content'),
      utm_term: get('utm_term'),
      locale: document.documentElement.lang || '',
      timezone: (window.Intl && Intl.DateTimeFormat) ? Intl.DateTimeFormat().resolvedOptions().timeZone || '' : '',
      viewport: window.innerWidth + 'x' + window.innerHeight,
      user_agent: navigator.userAgent || ''
    };
  }

  function fingerprint(cart) {
    return ((cart && cart.items) || []).map(function(item) {
      return String(item.variant_id || item.id) + ':' + String(item.quantity || 1);
    }).sort().join('|');
  }

  function buildRecord(email, consent, cart) {
    var id = randomId();
    var recoveryUrl = createRecoveryUrl(cart);
    var oneCode = root.getAttribute('data-one-code') || '10OFF';
    var twoCode = root.getAttribute('data-two-code') || '20OFF';
    var rugs = rugCount(cart);
    return {
      schema: 'jh.saved-cart.v2.2',
      id: id,
      created_at: new Date().toISOString(),
      expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      shop: root.getAttribute('data-shop-name') || '',
      shop_url: window.location.origin,
      source: 'jaipur-handloom-theme-saved-cart',
      page_url: window.location.href,
      referrer: document.referrer || '',
      email: email,
      marketing_consent: Boolean(consent),
      currency: cart.currency || root.getAttribute('data-currency') || '',
      cart_token: cart.token || '',
      item_count: Number(cart.item_count || 0),
      total_price: Number(cart.total_price || 0),
      total_display: money(cart.total_price),
      rug_count: rugs,
      offer_code: rugs >= 2 ? twoCode : oneCode,
      recovery_url: recoveryUrl,
      recovery_url_10off: createDiscountUrl(oneCode, recoveryUrl),
      recovery_url_20off: createDiscountUrl(twoCode, recoveryUrl),
      checkout_url: window.location.origin + (root.getAttribute('data-checkout-url') || route('checkout')),
      whatsapp_number: root.getAttribute('data-whatsapp-number') || '',
      fingerprint: fingerprint(cart),
      attribution: attribution(),
      collections: collectionDirectory(),
      items: serializeItems(cart),
      website: ''
    };
  }

  function merchantBody(record) {
    var lines = [
      'Saved cart recovery capture',
      '',
      'Recovery ID: ' + record.id,
      'Email: ' + record.email,
      'Marketing consent: ' + (record.marketing_consent ? 'Yes' : 'No'),
      'Created: ' + record.created_at,
      'Cart token: ' + record.cart_token,
      'Items: ' + record.item_count,
      'Rug quantity: ' + record.rug_count,
      'Cart total: ' + record.total_display,
      'Suggested offer: ' + record.offer_code,
      'UTM source: ' + (record.attribution.utm_source || ''),
      'UTM campaign: ' + (record.attribution.utm_campaign || ''),
      '',
      'Portable recovery link:',
      record.recovery_url,
      '',
      '10OFF recovery link:',
      record.recovery_url_10off,
      '',
      '20OFF recovery link:',
      record.recovery_url_20off,
      '',
      'Cart contents:'
    ];
    record.items.forEach(function(item) {
      lines.push(item.quantity + ' x ' + item.title + (item.variant ? ' - ' + item.variant : '') + ' - ' + item.url);
    });
    lines.push('', 'Source page: ' + record.page_url, 'Schema: ' + record.schema);
    return lines.join('\n');
  }

  function submitLiquidForm(form) {
    if (!form || !form.action) return Promise.resolve({ skipped: true });
    return withTimeout(fetch(form.action, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'text/html,application/xhtml+xml' },
      body: new FormData(form),
      redirect: 'follow'
    }), 9000, 'Shopify form request timed out').then(function(response) {
      if (!response.ok) throw new Error('Shopify form request failed: ' + response.status);
      return { ok: true, url: response.url || '' };
    });
  }

  function saveMerchantCopy(record) {
    if (root.getAttribute('data-send-merchant-copy') === 'false') return Promise.resolve({ skipped: true });
    var form = qs('#JHSavedCartMerchantCapture', root);
    if (!form) return Promise.resolve({ skipped: true });
    var email = qs('[data-jh-saved-cart-merchant-email]', form);
    var tags = qs('[data-jh-saved-cart-merchant-tags]', form);
    var body = qs('[data-jh-saved-cart-merchant-body]', form);
    if (email) email.value = record.email;
    if (tags) tags.value = 'saved-cart,recovery-v2.3,' + (record.rug_count >= 2 ? 'two-rug-offer' : 'standard-offer');
    if (body) body.value = merchantBody(record);
    return submitLiquidForm(form);
  }

  function saveCustomerConsent(record) {
    if (!record.marketing_consent) return Promise.resolve({ skipped: true });
    var form = qs('#JHSavedCartCustomerCapture', root);
    if (!form) return Promise.resolve({ skipped: true });
    var email = qs('[data-jh-saved-cart-customer-email]', form);
    if (email) email.value = record.email;
    return submitLiquidForm(form);
  }

  function saveCartAttributes(record) {
    return withTimeout(fetch(route('cart/update.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: {
          'Saved cart recovery ID': record.id,
          'Saved cart email': record.email,
          'Saved cart consent': record.marketing_consent ? 'Marketing consent provided' : 'Cart service only',
          'Saved cart item count': String(record.item_count),
          'Saved cart rug count': String(record.rug_count),
          'Saved cart suggested offer': record.offer_code,
          'Saved cart source': 'Theme recovery v2.2'
        }
      })
    }), 8000, 'Cart attribute update timed out').then(function(response) {
      if (!response.ok) throw new Error('Could not update cart attributes');
      return response.json();
    });
  }

  function postEndpoint(record) {
    var endpoint = String(root.getAttribute('data-endpoint') || '').trim();
    if (!endpoint) return Promise.resolve({ skipped: true });
    return withTimeout(fetch(endpoint, {
      method: 'POST',
      mode: endpoint.charAt(0) === '/' ? 'same-origin' : 'cors',
      credentials: endpoint.charAt(0) === '/' ? 'same-origin' : 'omit',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-JH-Saved-Cart-Version': '2.2'
      },
      body: JSON.stringify(record),
      keepalive: true
    }), 12000, 'Recovery email endpoint timed out').then(function(response) {
      if (!response.ok) throw new Error('Recovery endpoint failed: ' + response.status);
      return response.json().catch(function() { return { ok: true, email_queued: true }; });
    });
  }

  function allSettled(promises) {
    if (Promise.allSettled) return Promise.allSettled(promises);
    return Promise.all(promises.map(function(promise) {
      return Promise.resolve(promise).then(function(value) { return { status: 'fulfilled', value: value }; }, function(reason) { return { status: 'rejected', reason: reason }; });
    }));
  }

  function applyEndpointResponse(record, response) {
    response = response || {};
    if (response.recovery_url) record.recovery_url = absoluteUrl(response.recovery_url);
    if (response.recovery_url_10off) record.recovery_url_10off = absoluteUrl(response.recovery_url_10off);
    if (response.recovery_url_20off) record.recovery_url_20off = absoluteUrl(response.recovery_url_20off);
    if (response.id) record.server_id = String(response.id);
    return record;
  }

  function completeSuccess(record, deliveryState, endpointResponse) {
    record = applyEndpointResponse(record, endpointResponse);
    state.record = record;
    writeStorage(record);
    var email = qs('[data-jh-saved-cart-success-email]', root);
    var returnLink = qs('[data-jh-saved-cart-return]', root);
    var delivery = qs('[data-jh-saved-cart-delivery-status]', root);
    var whatsappShare = qs('[data-jh-saved-cart-whatsapp-share]', root);
    var whatsappHelp = qs('[data-jh-saved-cart-whatsapp-help]', root);
    if (email) email.textContent = record.email;
    if (returnLink) returnLink.href = record.recovery_url;
    if (whatsappShare) whatsappShare.href = 'https://wa.me/?text=' + encodeURIComponent('My Jaipur Handloom cart: ' + record.recovery_url);
    if (whatsappHelp) {
      var number = String(record.whatsapp_number || '').replace(/\D/g, '');
      whatsappHelp.href = 'https://wa.me/' + number + '?text=' + encodeURIComponent('Hi Jaipur Handloom, I need help choosing the right size or variation for this saved cart: ' + record.recovery_url);
    }
    if (delivery) {
      delivery.setAttribute('data-state', deliveryState);
      if (deliveryState === 'endpoint-ok') {
        delivery.textContent = 'Your saved-cart email has been accepted for delivery to ' + record.email + '.';
      } else if (deliveryState === 'endpoint-retry') {
        delivery.textContent = 'Your cart is safely stored. The first email attempt was delayed, and the recovery service scheduled an automatic retry. You can also copy or share the link below.';
      } else if (deliveryState === 'endpoint-failed') {
        delivery.textContent = 'Your cart and return link are saved, but the email delivery service did not accept this request. Copy or share the cart link below while the endpoint is checked.';
      } else if (deliveryState === 'duplicate') {
        delivery.textContent = 'This same cart was already saved recently. We reused the existing recovery link to avoid duplicate messages.';
      } else if (isDesignMode()) {
        delivery.textContent = 'Preview test completed: the cart and recovery link were saved. No customer email was sent because a recovery endpoint is not connected.';
      } else {
        delivery.textContent = 'Your cart and return link are saved. A customer email was not sent because saved-cart email delivery is not connected.';
      }
    }
    setView('success');
    updateReopen(record);
    emit('jh:saved-cart:captured', record);
    track('saved_cart_success', {
      recovery_id: record.id,
      item_count: record.item_count,
      rug_count: record.rug_count,
      total_price: record.total_price,
      email_delivery: deliveryState,
      marketing_consent: record.marketing_consent
    });
  }

  function submit(event) {
    event.preventDefault();
    if (state.busy) return;
    showError('');
    var input = qs('#JHSavedCartEmail', root);
    var consent = qs('[data-jh-saved-cart-consent]', root);
    var honeypot = qs('[data-jh-saved-cart-honeypot]', root);
    var email = input ? input.value.trim() : '';
    if (honeypot && honeypot.value) return;
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showError('Please enter a valid email address.');
      if (input) input.focus();
      return;
    }

    setBusy(true);
    track('saved_cart_submit', { marketing_consent: Boolean(consent && consent.checked), endpoint_connected: endpointConfigured() });
    getCart().then(function(cart) {
      if (!cart || !cart.items || !cart.items.length) throw new Error('Your cart is empty.');
      renderCart(cart);
      var record = buildRecord(email, consent && consent.checked, cart);
      var previous = readStorage();
      if (previous && previous.email === record.email && previous.fingerprint === record.fingerprint && previous.created_at && (Date.now() - Date.parse(previous.created_at) < 60000)) {
        completeSuccess(previous, 'duplicate');
        return null;
      }
      return allSettled([
        saveCartAttributes(record),
        saveMerchantCopy(record),
        saveCustomerConsent(record),
        postEndpoint(record)
      ]).then(function(results) {
        var endpointResult = results[3];
        var endpointFailed = endpointConfigured() && endpointResult && endpointResult.status === 'rejected';
        var endpointFulfilled = endpointConfigured() && endpointResult && endpointResult.status === 'fulfilled' && endpointResult.value && endpointResult.value.skipped !== true;
        var endpointResponse = endpointFulfilled ? endpointResult.value : null;
        var deliveryState = 'no-endpoint';
        if (endpointFailed) deliveryState = 'endpoint-failed';
        else if (endpointFulfilled && endpointResponse.email_queued === false && endpointResponse.retry_scheduled) deliveryState = 'endpoint-retry';
        else if (endpointFulfilled) deliveryState = endpointResponse.duplicate ? 'duplicate' : 'endpoint-ok';
        completeSuccess(record, deliveryState, endpointResponse);
        if (endpointFailed) {
          var status = qs('[data-jh-saved-cart-copy-status]', root);
          if (status) status.textContent = 'Email service error: the cart is safe locally. Copy or share the recovery link.';
          track('saved_cart_email_delivery_failed', { recovery_id: record.id });
        }
      });
    }).catch(function(error) {
      showError(error && error.message ? error.message : 'We could not save your cart. Please try again.');
      track('saved_cart_save_error', { message: error && error.message ? error.message : 'Unknown save error' });
    }).then(function() {
      setBusy(false);
    }, function(error) {
      setBusy(false);
      throw error;
    });
  }

  function copyLink() {
    var record = state.record || readStorage();
    var status = qs('[data-jh-saved-cart-copy-status]', root);
    if (!record || !record.recovery_url) return;
    var copy = navigator.clipboard && navigator.clipboard.writeText ? navigator.clipboard.writeText(record.recovery_url) : Promise.reject(new Error('Clipboard unavailable'));
    copy.then(function() {
      if (status) status.textContent = 'Cart link copied.';
      track('saved_cart_copy_link', { recovery_id: record.id || '' });
    }).catch(function() {
      window.prompt('Copy this cart link:', record.recovery_url);
      if (status) status.textContent = 'Cart link ready to copy.';
    });
  }

  function nativeShare() {
    var record = state.record || readStorage();
    if (!record || !record.recovery_url) return;
    if (navigator.share) {
      navigator.share({ title: 'My Jaipur Handloom cart', text: 'Here is my saved Jaipur Handloom cart.', url: record.recovery_url }).catch(function() {});
    } else {
      copyLink();
    }
  }

  function updateReopen(record) {
    var button = qs('[data-jh-saved-cart-reopen]', root);
    var count = qs('[data-jh-saved-cart-reopen-count]', root);
    if (!button) return;
    if (record && record.items && record.items.length) {
      button.hidden = false;
      if (count) count.textContent = String(record.item_count || record.items.length);
    } else {
      button.hidden = true;
    }
  }

  function addRestoreItems(items) {
    if (!items.length) return Promise.resolve();
    if (JH.Cart && typeof JH.Cart.addItems === 'function') return withTimeout(JH.Cart.addItems(items, { openDrawer: false }), 8000, 'Cart restore add timed out');
    return withTimeout(fetch(route('cart/add.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }), 8000, 'Cart restore add timed out').then(function(response) {
      if (!response.ok) throw new Error('Could not restore saved cart');
      return response.json();
    });
  }

  function stableProperties(properties) {
    properties = properties || {};
    return Object.keys(properties).sort().map(function(key) {
      return key + ':' + String(properties[key]);
    }).join('|');
  }

  function lineFingerprint(item) {
    return String(item.variant_id || item.id) + '::' + stableProperties(item.properties || item.p);
  }

  function changeRestoreItem(key, quantity) {
    if (JH.Cart && typeof JH.Cart.change === 'function') return withTimeout(JH.Cart.change(key, quantity), 8000, 'Cart restore quantity timed out');
    return withTimeout(fetch(route('cart/change.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }), 8000, 'Cart restore quantity timed out').then(function(response) {
      if (!response.ok) throw new Error('Could not update restored cart quantity');
      return response.json();
    });
  }

  function restoreFromUrl() {
    var url = new URL(window.location.href);
    var encoded = url.searchParams.get(RESTORE_PARAM);
    if (!encoded || state.restoredOnLoad) return Promise.resolve(false);
    state.restoredOnLoad = true;
    var payload;
    try { payload = JSON.parse(base64UrlDecode(encoded)); } catch (error) { return Promise.resolve(false); }
    var items = (payload.i || []).map(function(line) {
      var item = { id: Number(line.id), quantity: Math.max(1, Number(line.q || 1)) };
      if (line.p) item.properties = line.p;
      if (line.s) item.selling_plan = line.s;
      return item;
    }).filter(function(item) { return item.id > 0; });
    if (!items.length) return Promise.resolve(false);

    return getCart().then(function(cart) {
      var existing = {};
      (cart.items || []).forEach(function(item) { existing[lineFingerprint(item)] = item; });
      var missing = [];
      var quantityChanges = [];
      items.forEach(function(item) {
        var current = existing[lineFingerprint(item)];
        if (!current) {
          missing.push(item);
        } else if (Number(current.quantity || 0) !== Number(item.quantity || 1)) {
          quantityChanges.push(changeRestoreItem(current.key || current.variant_id || current.id, item.quantity));
        }
      });
      return Promise.all(quantityChanges).then(function() { return addRestoreItems(missing); });
    }).then(function() {
      url.searchParams.delete(RESTORE_PARAM);
      window.history.replaceState({}, '', url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') + url.hash);
      emit('jh:saved-cart:restored', { items: items });
      track('saved_cart_restore', { item_lines: items.length });
      if (JH.Cart && typeof JH.Cart.refresh === 'function') return JH.Cart.refresh();
      return true;
    }).catch(function() {
      return false;
    });
  }

  function bind() {
    state.cart = readInitialCart() || state.cart;
    if (state.cart) renderCart(state.cart);
    setLoadingState('ready');
    root.setAttribute('data-jh-saved-cart-ready', 'true');

    var configuredLabel = qs('[data-jh-saved-cart-submit-label]', root);
    if (configuredLabel) root.setAttribute('data-configured-button-label', configuredLabel.textContent.trim());
    configureDeliveryReadiness();

    document.addEventListener('click', function(event) {
      var openButton = event.target.closest && event.target.closest('[data-jh-save-cart-open],[data-jh-saved-cart-reopen]');
      if (openButton) {
        event.preventDefault();
        open(openButton);
        return;
      }
      if (event.target.closest && event.target.closest('[data-jh-saved-cart-close]')) {
        event.preventDefault();
        close();
        return;
      }
      if (event.target.closest && event.target.closest('[data-jh-saved-cart-copy]')) {
        event.preventDefault();
        copyLink();
        return;
      }
      if (event.target.closest && event.target.closest('[data-jh-saved-cart-native-share]')) {
        event.preventDefault();
        nativeShare();
        return;
      }
      if (event.target.closest && event.target.closest('[data-jh-saved-cart-retry]')) {
        event.preventDefault();
        loadCartForModal({ focus: true }).catch(function() {});
      }
    });

    var form = qs('[data-jh-saved-cart-form]', root);
    if (form) form.addEventListener('submit', submit);

    document.addEventListener('keydown', function(event) {
      var modal = qs('[data-jh-saved-cart-modal]', root);
      if (!modal || modal.hidden) return;
      if (event.key === 'Escape') close();
      else if (event.key === 'Tab' && typeof A11y.trapTab === 'function') A11y.trapTab(event, modal);
    });

    document.addEventListener('jh:cart:changed', function(event) {
      var supplied = event && event.detail && (event.detail.cart || event.detail);
      if (supplied && Array.isArray(supplied.items)) state.cart = supplied;
      var modal = qs('[data-jh-saved-cart-modal]', root);
      if (modal && !modal.hidden) loadCartForModal({ focus: false }).catch(function() {});
    });

    updateReopen(readStorage());
    restoreFromUrl();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind, { once: true });
  else bind();
})();
